import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";

// ==========================================
// 📦 STATIC IMPORTS (Layouts & Middleware)
// ==========================================
import AdminLayout from "./components/layout/AdminLayout";
import ProtectedRoute from "./middleware/ProtectedRoute";

// Global CSS (Toastify stays global, Quill is removed from here)
import "react-toastify/dist/ReactToastify.css";

// Global Search popup (mounted globally so Ctrl/Cmd+K works from anywhere)
import GlobalSearchPopup from "./popups/GlobalSearchPopup";

// ==========================================
// 🚀 LAZY LOADED COMPONENTS (Code Splitting)
// ==========================================

// Auth & Core Public Pages
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const CompanySignup = lazy(() => import("./pages/auth/CompanySignup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const OTP = lazy(() => import("./pages/auth/OTP"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const ViewInvoice = lazy(() => import("./pages/invoices/ViewInvoice"));
const PublicInvoiceView = lazy(() => import("./pages/invoices/PublicInvoiceView"));

// Dashboard & Analytics
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));

// Banks & Taxes
const BanksList = lazy(() => import("./pages/banks/BanksList"));
const CreateBank = lazy(() => import("./pages/banks/CreateBank"));
const UpdateBank = lazy(() => import("./pages/banks/UpdateBank"));
const TaxesList = lazy(() => import("./pages/taxes/TaxesList"));
const CreateTax = lazy(() => import("./pages/taxes/CreateTax"));
const UpdateTax = lazy(() => import("./pages/taxes/UpdateTax"));

// Items
const ItemsList = lazy(() => import("./pages/items/ItemsList"));
const CreateItem = lazy(() => import("./pages/items/CreateItem"));
const UpdateItem = lazy(() => import("./pages/items/UpdateItem"));

// Discount Coupons
const CouponsList = lazy(() => import("./pages/coupons/CouponsList"));
const CreateCoupon = lazy(() => import("./pages/coupons/CreateCoupon"));
const UpdateCoupon = lazy(() => import("./pages/coupons/UpdateCoupon"));

// Invoices & Transactions
const InvoicesList = lazy(() => import("./pages/invoices/InvoicesList"));
const InvoiceWithTaxList = lazy(() =>
  import("./pages/invoices/InvoiceWithTaxList")
);
const InvoiceWithoutTaxList = lazy(() =>
  import("./pages/invoices/InvoiceWithoutTaxList")
);
const CreateInvoice = lazy(() => import("./pages/invoices/CreateInvoice"));
const UpdateInvoice = lazy(() => import("./pages/invoices/UpdateInvoice"));
const TransactionsList = lazy(() =>
  import("./pages/transactions/TransactionsList")
);
const TransactionDashboard = lazy(() =>
  import("./pages/transactions/TransactionDashboard")
);
const CreateTransaction = lazy(() =>
  import("./pages/transactions/CreateTransaction")
);
const UpdateTransaction = lazy(() =>
  import("./pages/transactions/UpdateTransaction")
);

// Expenses
const ExpenseDashboard = lazy(() =>
  import("./pages/expenses/ExpenseDashboard")
);
const ExpenseCategoriesList = lazy(() =>
  import("./pages/expenses/ExpenseCategoriesList")
);
const VendorsList = lazy(() =>
  import("./pages/expenses/VendorsList")
);

// Customers
const CustomersList = lazy(() => import("./pages/customers/CustomersList"));
const CreateCustomer = lazy(() => import("./pages/customers/CreateCustomer"));
const UpdateCustomer = lazy(() => import("./pages/customers/UpdateCustomer"));
const CustomerDetails = lazy(() => import("./pages/customers/CustomerDetails"));

// Companies
const CompaniesList = lazy(() => import("./pages/companies/CompaniesList"));
const CreateCompany = lazy(() => import("./pages/companies/CreateCompany"));
const UpdateCompany = lazy(() => import("./pages/companies/UpdateCompany"));

// Plans
const PlansList = lazy(() => import("./pages/plans/PlansList"));
const CreatePlan = lazy(() => import("./pages/plans/CreatePlan"));
const UpdatePlan = lazy(() => import("./pages/plans/UpdatePlan"));


// Miscellaneous / Settings
const Setting = lazy(() => import("./pages/others/Setting"));
const CompanySettings = lazy(() => import("./pages/others/CompanySettings"));
const Profile = lazy(() => import("./pages/others/Profile"));
const NotFound = lazy(() => import("./pages/others/NotFound"));

export default function App() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-gray-50 text-gray-500 font-medium">
          Loading system...
        </div>
      }
    >
      {/* Global Search popup (always mounted so Ctrl/Cmd+K works everywhere) */}
      <GlobalSearchPopup />

      <Routes>
        {/* ========== Public Routes ========== */}
        <Route index element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/company-signup/:token" element={<CompanySignup />} />
        <Route path="/verify-otp" element={<OTP />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/invoice/view" element={<ViewInvoice />} />
        <Route path="/:company_slug/:invoice_id/:token" element={<PublicInvoiceView />} />

        {/* ========== Protected Routes ========== */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<AdminLayout />}>
            <Route path="dashboard" element={<Dashboard />} />

            {/* Banks */}
            <Route path="banks" element={<BanksList />} />
            <Route path="bank/create" element={<CreateBank />} />
            <Route path="bank/update" element={<UpdateBank />} />

            {/* Taxes */}
            <Route path="taxes" element={<TaxesList />} />
            <Route path="tax/create" element={<CreateTax />} />
            <Route path="tax/update" element={<UpdateTax />} />

            {/* Items */}
            <Route path="items" element={<ItemsList />} />
            <Route path="item/create" element={<CreateItem />} />
            <Route path="item/update" element={<UpdateItem />} />

            {/* Coupons */}
            <Route path="coupons" element={<CouponsList />} />
            <Route path="coupon/create" element={<CreateCoupon />} />
            <Route path="coupon/update" element={<UpdateCoupon />} />

            {/* Invoices */}
            <Route path="invoices" element={<InvoicesList />} />
            <Route
              path="invoices/with-tax"
              element={<InvoiceWithTaxList />}
            />
            <Route
              path="invoices/without-tax"
              element={<InvoiceWithoutTaxList />}
            />
            <Route path="invoice/create" element={<CreateInvoice />} />
            <Route path="invoice/update" element={<UpdateInvoice />} />

            {/* Transactions */}
            <Route path="transactions" element={<TransactionsList />} />
            <Route path="transaction/dashboard" element={<TransactionDashboard />} />
            <Route path="transaction/create" element={<CreateTransaction />} />
            <Route path="transaction/update" element={<UpdateTransaction />} />

            {/* Expenses */}
            <Route path="expenses" element={<ExpenseDashboard />} />
            <Route path="expense-categories" element={<ExpenseCategoriesList />} />
            <Route path="vendors" element={<VendorsList />} />

            {/* Customers */}
            <Route path="customers" element={<CustomersList />} />
            <Route path="customer/create" element={<CreateCustomer />} />
            <Route path="customer/update" element={<UpdateCustomer />} />
            <Route path="customer/details" element={<CustomerDetails />} />

            {/* Companies */}
            <Route path="companies" element={<CompaniesList />} />
            <Route path="company/create" element={<CreateCompany />} />
            <Route path="company/update" element={<UpdateCompany />} />

            {/* Plans */}
            <Route path="plans" element={<PlansList />} />
            <Route path="plan/create" element={<CreatePlan />} />
            <Route path="plan/update" element={<UpdatePlan />} />

            {/* Others */}
            <Route path="settings" element={<Setting />} />
            <Route path="company-settings" element={<CompanySettings />} />
            <Route path="profile" element={<Profile />} />
          </Route>
        </Route>

        {/* ========== 404 Fallback ========== */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
